import { Injectable } from '@angular/core';
import { HorasExtraActiva } from '../interfaces/schedule.interface';
import {
  EventsPayloadV2,
  EventDatumV2,
  MetaDatumV2,
  PermisoV2,
  PausaV2,
  SessionV2,
  WorkerProfileV2,
  JornadaAnteriorV2,
} from '../interfaces/scheadule-v2.interface';
import { ClosedSubEvent, EventNeo, MetaNeo, OpenSubEvent, PreviousWorkDay, UserLoggedFromEvent, WorkerCurrent } from '../interfaces/ficha.interface';

@Injectable({
  providedIn: 'root'
})
export class FichaJsonBuildV2Service {

  eventsNeo(events: EventsPayloadV2 | undefined) {
    if (events === undefined) return;
    return {
      meta: this.setMetaNeo(events),
      data: this.setEventNeoArr(events.data, events.session.worker_profile),
    };
  }

  // #region BUILD META NEO

  private setMetaNeo(events: EventsPayloadV2): MetaNeo {
    const meta = (events.meta ?? {}) as Partial<MetaDatumV2>;
    const profile = events.session?.worker_profile;
    const eventsDatum = Array.isArray(events.data) ? events.data : [];

    if (!profile) {
      throw new Error('[FichaJsonBuildV2Service] Missing session.worker_profile in canonical payload');
    }

    const metaNeoBuild: MetaNeo = {
      // FICHA SETTINGS
      previous_work_day:                  !meta.jornada_anterior ? null : this.setMetaPreviousWorkDay(meta.jornada_anterior),
      previous_work_day_without_clocking: !!meta.evento_anterior_sin_fichaje,

      // FICHA STATES
      open_leave:    !meta.permisos_del_dia_activos ? null : this.setMetaOpenLeave(meta.permisos_del_dia_activos),
      open_overtime: !meta.horas_extras_activas     ? null : this.setMetaOpenOverTime(meta.horas_extras_activas),
      open_pause:    !meta.pausas_activas            ? null : this.setMetaOpenPause(meta.pausas_activas),

      // SUB EVENTS
      closed_leaves: this.setMetaClosedLeaveArr(meta.permisos_del_dia_finalizados ?? []),
      closed_pauses: this.setMetaClosedPauseArr(meta.pausas_finalizadas ?? []),

      // WORKER IDENTITY — ahora viene de meta (ya no repetido en cada evento)
      company_id:  meta.company_id  ?? 0,
      worker_dni:  meta.dni         ?? '',
      worker_name: meta.name        ?? '',

      // Capa 1: exposicion temporal de campos nuevos de meta v2.
      meta_v2: {
        has_crossday_open_events: meta.has_crossday_open_events,
        jornada_abierta_anterior: !meta.jornada_abierta_anterior
          ? null
          : this.setMetaPreviousWorkDay(meta.jornada_abierta_anterior),
        informe_firmado: meta.informe_firmado,
        opcionales: meta.opcionales ?? null,
        centros_trabajo: meta.centros_trabajo ?? [],
      },
    };

    metaNeoBuild.worker_current = this.setWorkerCurrent(meta, profile);
    const capabilities = this.setMetaUserLoggedFromProfile(meta, profile, events.session, eventsDatum);
    metaNeoBuild.capabilities = capabilities;

    return metaNeoBuild;
  }

  private setMetaPreviousWorkDay(jornadaAnterior: JornadaAnteriorV2): PreviousWorkDay {
    return {
      uuid:                 jornadaAnterior.uuid,
      // date_start/date_end aún no son ISO en backend — se sigue usando convertToLocalISOFormat
      event_start_expected: this.convertToLocalISOFormat(jornadaAnterior.date_start),
      event_end_expected:   this.convertToLocalISOFormat(jornadaAnterior.date_end),
    };
  }

  private setMetaOpenLeave(permiso: PermisoV2): OpenSubEvent {
    return {
      id:             permiso.id,           // ya es number (antes: Number.parseInt(string))
      schedule_id:    permiso.schedule_id,  // typo corregido (antes: shedule_id string)
      motivo_id:      permiso.reason.id,    // antes: Number.parseInt(motivo_id)
      request_id:     permiso.request_id,

      //! PENDIENTE backend: starts_at es la hora planificada, check_in_at es el fichaje real.
      //! Cuando backend confirme fix del bug de nomenclatura, cambiar a: permiso.check_in_at ?? permiso.starts_at
      date_in:        permiso.starts_at,

      motivo_request: permiso.reason.name,  // antes: nombre (campo plano)
      comment:        permiso.comment,      // antes: comments
    };
  }

  private setMetaOpenOverTime(horasExtraActivas: HorasExtraActiva): OpenSubEvent {
    // HorasExtraActiva aún no refactorizada por backend — misma lógica que V1
    if (
      horasExtraActivas.mapa_in === null
      && horasExtraActivas.latitude_in !== null
      && horasExtraActivas.longitude_in !== null
    ) {
      console.warn('[FichaJsonBuildV2Service] Missing map URL in active overtime response', {
        request_id: horasExtraActivas.request_id,
        schedule_id: horasExtraActivas.schedule_id,
        latitude_in: horasExtraActivas.latitude_in,
        longitude_in: horasExtraActivas.longitude_in,
      });
    }


    return {
      id:           horasExtraActivas.id,
      schedule_id:  horasExtraActivas.schedule_id,
      request_id:   horasExtraActivas.request_id,
      date_in:      horasExtraActivas.day + ' ' + horasExtraActivas.date_start,
      motivo_request: horasExtraActivas.comments,

      event_start_map_img:  horasExtraActivas.mapa_in,
      event_start_latitud:  horasExtraActivas.latitude_in,
      event_start_longitud: horasExtraActivas.longitude_in,
    };
  }

  private setMetaOpenPause(pausa: PausaV2): OpenSubEvent {
    return {
      id:             pausa.id,           // ya es number (antes: parseInt(string))
      schedule_id:    pausa.schedule_id,  // typo corregido (antes: shedule_id string)
      motivo_id:      pausa.reason.id,    // antes: parseInt(motivo_id)

      //! PENDIENTE backend: starts_at es la hora planificada, check_in_at es el fichaje real.
      //! Cuando backend confirme fix, cambiar a: pausa.starts_at (o check_in_at cuando exista)
      date_in:        pausa.starts_at,

      motivo_request: pausa.reason.name,  // antes: motivo (campo plano)
      comment:        pausa.comment,      // antes: comentario
    };
  }

  private setMetaClosedLeaveArr(permisosFinalizados: PermisoV2[]): ClosedSubEvent[] {
    const leaveArr: ClosedSubEvent[] = [];
    for (const el of permisosFinalizados) {
      leaveArr.push({
        id:           el.id,
        schedule_id:  el.schedule_id,       // typo corregido (antes: shedule_id ?? 0)
        request_id:   el.request_id,

        //! PENDIENTE backend: check_in_at es el fichaje real, starts_at es el planificado.
        //! Cuando backend confirme fix, cambiar a: el.check_in_at ?? ''
        date_in:      el.check_in_at ?? '',
        date_out:     el.check_out_at ?? null,
        date_start:   el.starts_at,         // ya es ISO — NO pasar por convertToLocalISOFormat
        date_end:     el.ends_at ?? null,

        motivo_request:  el.reason.name,    // antes: permiso_motivo_nombre (campo plano)
        motivo_id:       el.reason.id,
        comment:         el.comment,        // antes: comentario_permiso

        elapsed_minutes: el.duration_minutes, // antes: tiempo_real (era negativo en V1, ahora positivo)

        // imagen_permiso era string numérico ('7'/'0') en V1; ahora es number en attachments
        attached_img: String(el.attachments.images), // mantiene tipo string de ClosedSubEvent
        attached_pdf: String(el.attachments.pdfs),
      });
    }
    return leaveArr;
  }

  private setMetaClosedPauseArr(pausasFinalizadas: PausaV2[]): ClosedSubEvent[] {
    const pauseArr: ClosedSubEvent[] = [];
    for (const el of pausasFinalizadas) {
      pauseArr.push({
        id:           el.id,                // antes: Number.parseInt(pausa_id string)
        schedule_id:  el.schedule_id,       // typo corregido (antes: shedule_id)
        motivo_id:    el.reason.id,         // antes: Number.parseInt(pausa_motivo_id string)

        motivo_request: el.reason.name,     // antes: pausa_motivo (campo plano)
        comment:        el.comment,         // antes: comentario_pausa

        //! PENDIENTE backend: starts_at es la hora planificada, check_in_at es el fichaje real.
        //! Cuando backend confirme fix, cambiar a: el.starts_at (o check_in_at cuando exista)
        date_in:  el.starts_at,             // ya es ISO — NO pasar por convertToLocalISOFormat
        date_out: el.ends_at ?? null,

        elapsed_seconds: el.duration_seconds, // antes: segundos
      });
    }
    return pauseArr;
  }

  private setWorkerCurrent(meta: Partial<MetaDatumV2>, profile: WorkerProfileV2): WorkerCurrent {
    return {
      // worker_id canónico desde session.worker_profile.
      worker_id:    profile.worker_id,
      worker_name:  meta.name           ?? '',
      company_id:   meta.company_id     ?? 0,
      worker_dni:   meta.dni            ?? '',
      company_name: 'company name pending',
    };
  }

  private setMetaUserLoggedFromProfile(
    meta: Partial<MetaDatumV2>,
    profile: WorkerProfileV2,
    session: SessionV2,
    eventsDatum: EventDatumV2[],
  ): UserLoggedFromEvent {
    const allowFullDayLeave = eventsDatum.some((ev) => !!ev.permiso_dia_completo);
    const profileLegacy = profile as unknown as Record<string, unknown>;
    const sessionLegacy = session as unknown as Record<string, unknown>;
    const workerConfig = session.worker_config ?? {};
    const allowFreeWork = this.pickBoolean(
      workerConfig.trabajo_libre,
      profile.trabajoLibre,
      session.trabajoLibre,
      profileLegacy['trabajo_libre'] as boolean | undefined,
      sessionLegacy['trabajo_libre'] as boolean | undefined,
    );
    const allowClockWithoutSchedule = this.pickBoolean(
      workerConfig.fichaje_sin_agenda,
      profile.fichajeSinAgenda,
      session.fichajeSinAgenda,
      profileLegacy['fichaje_sin_agenda'] as boolean | undefined,
      sessionLegacy['fichaje_sin_agenda'] as boolean | undefined,
    );
    const allowPause = this.pickBoolean(
      profile.pausas,
      session.mostrarPausas,
      profileLegacy['mostrarPausas'] as boolean | undefined,
      sessionLegacy['mostrar_pausas'] as boolean | undefined,
    );
    const allowWebAccess = this.pickBoolean(
      workerConfig.acceso_web,
      session.acceso_web,
      sessionLegacy['acceso_web'] as boolean | undefined,
    );

    // Antes leía de eventsDatum[0] (campos repetidos en cada evento).
    // Ahora lee de session.worker_profile (centralizado) + meta.informe_firmado.
    return {
      is_informe_firmado:  meta.informe_firmado         ?? false, // antes: ev.informe_firmado
      pauses_computable:   profile.pausa_computable,
      require_signature:   profile.firma_requerida,
      require_location:    profile.mapa_requerido_fichar,
      allow_remote_work:   profile.teletrabajo_dia,
      allow_location:      profile.include_location,
      allow_change_permisos: profile.modificar_permisos,
      allow_overtime:      profile.horas_extras_jornada,
      allow_pause:         allowPause,
      allow_leave:         profile.permisos_retribuidos_jornada,
      // no viene en worker_profile; fallback a evidencia en eventos del dia.
      allow_full_day_leave: allowFullDayLeave,

      allow_event_start_limit_adjustment: profile.ajuste_fichaje_entrada,
      allow_event_end_limit_adjustment:   profile.ajuste_fichaje,
      allow_free_work: allowFreeWork,
      allow_clock_without_schedule: allowClockWithoutSchedule,
      allow_web_access: allowWebAccess,
    };
  }

  private pickBoolean(...values: Array<boolean | undefined>): boolean {
    for (const value of values) {
      if (typeof value === 'boolean') return value;
    }
    return false;
  }

  // #endregion


  // #region BUILD EVENTS NEO

  private setEventNeoArr(eventsDatum: EventDatumV2[], profile?: WorkerProfileV2): EventNeo[] {
    if (eventsDatum.length < 1) return [];
    const eventNeoArrBuild: EventNeo[] = [];

    for (const ev of eventsDatum) {
      const evBuild: EventNeo = {
        // EVENT IDS
        id:                    ev.id,
        schedule_rule_id:      ev.horario_id,
        schedule_rule_line_id: ev.horario_linea_id,
        company_id:            ev.company_id,
        company_short_name:    profile?.short_name ?? '', // antes: ev.short_name (eliminado de EventDatumV2)

        // EVENT TYPE
        type:             ev.type,
        is_flexible:      ev.es_flexible,
        type_of_flexible: ev.tipo_flexible,
        show_estimate:    profile?.mostrar_estimadas ?? false, // antes: ev.mostrar_estimadas (eliminado de EventDatumV2)
        is_night_shift:   ev.es_nocturno,
        is_overtime:      ev.hora_extra,

        event_start_adjusted_by_management: ev.entrada_ajustada,
        event_adjusted_by_request:          ev.es_ajuste,

        // EVENT START
        event_start_clock_in:   ev.date_in,
        event_start_expected:   ev.date_start,
        event_start_tolerance:  ev.issue_time,
        event_start_control:    ev.entrance_time,
        event_start_limit:      ev.limite_entrada,

        // EVENT END
        event_end_clock_out:  ev.date_out,
        event_end_expected:   ev.date_end,
        event_end_tolerance:  ev.issue_time_exit,
        event_end_control:    ev.issue_time_exit, //! control y tolerancia de salida iguales por ahora
        event_end_limit:      ev.exit_time,

        // EVENT WORKING HOURS
        working_hours_penalty:   ev.penalizacion,
        working_hours_tolerance: ev.cortesia,
        working_hours_clocked:   ev.tiempo_real,
        working_hours_expected:  ev.tiempo_previsto,

        working_hours_expected_overtime: ev.tiempo_extra,    //! pendiente fusionar con expected
        working_hours_expected_default:  ev.jornada_estimada, //! pendiente fusionar con expected

        // EVENT LOCATION — event start
        event_start_location_prone_name: ev.estacion_alias_in,
        event_start_location_map_img:    ev.mapa_in,
        event_start_location_longitud:   ev.longitude_in,
        event_start_location_latitud:    ev.latitude_in,
        event_start_location_bio_name:   ev.location_in_alias,
        // EVENT LOCATION — event end
        event_end_location_prone_name:   ev.estacion_alias_out,
        event_end_location_map_img:      ev.mapa_out,
        event_end_location_longitude:    ev.longitude_out,
        event_end_location_latitude:     ev.latitude_out,
        event_end_location_bio_name:     ev.location_out_alias,

        // SUB EVENTS
        lunch:        ev.tiempo_comida,
        pauses_limit: profile?.pausa_limite ?? null, // antes: ev.pausa_limite (eliminado de EventDatumV2)

        // SUMMARY ELEMENTS
        attachments:   ev.adjuntos,
        img_number:    ev.images_permisos,
        pdfs_number:   ev.pdf_permisos,
        signature_img: ev.firma,
        remote_work:   ev.teletrabajo,

        // EVENT CLIENT ASSIGN
        client_assign_name: ev.cliente_name,
        client_assign_id:   ev.cliente_id,

        // INSTRUCTIONS & INCIDENCES
        instructions_by_management: ev.description,

        incidence_code: ev.tipo_incidencia,

        incidence_clock_in_id:       ev.issue_id_entrada,
        incidence_clock_in_reason:   ev.motivo_incidencia_entrada,
        incidence_clock_in_comment:  ev.comentario_incidencia_entrada,

        incidence_clock_out_id:      ev.issue_id_salida,
        incidence_clock_out_reason:  ev.motivo_incidencia_salida,
        incidence_clock_out_comment: ev.comentario_incidencia_salida,

        incidence_working_hours_id:      ev.issue_id_jornada,
        incidence_working_hours_reason:  ev.motivo_incidencia_jornada,
        incidence_working_hours_comment: ev.comentario_incidencia_jornada,
      };
      eventNeoArrBuild.push(evBuild);
    }

    return eventNeoArrBuild;
  }

  // #endregion


  // ######## HELPER FUNCTIONS ########

  // Usado SOLO para jornada_anterior.date_start / date_end — aún en formato no-ISO desde backend.
  // Las fechas de PermisoV2 y PausaV2 ya vienen en ISO 8601 y NO deben pasar por aquí.
  private convertToLocalISOFormat(dateStr: string | undefined): string {
    if (!dateStr) {
      console.error('date missing');
      return '';
    }
    const [datePart, timePart] = dateStr.split(' ');
    const [day, month, year] = datePart.split('-').map(Number);
    const [time] = timePart.split('.');
    const [hour, minute] = time.split(':').map(Number);
    const dateObj = new Date(year, month - 1, day, hour, minute);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${year}-${pad(month)}-${pad(day)} ${pad(hour)}:${pad(minute)}:${pad(dateObj.getSeconds())}`;
  }

}
